import { Link } from 'react-router-dom'
import { Helmet } from 'react-helmet-async'
import { ArrowLeft } from 'lucide-react'
import { Logo } from '../components/Logo'

const glassAlert = {
  background: 'rgba(142,143,250,0.05)',
  borderLeft: '2px solid rgba(142,143,250,0.6)',
  borderRadius: '8px',
  padding: '16px',
  marginBottom: '16px',
  boxShadow: 'inset 0 0 20px rgba(142,143,250,0.05)'
}

const glassAlertPink = {
  background: 'rgba(224,64,251,0.05)',
  borderLeft: '2px solid rgba(224,64,251,0.6)',
  borderRadius: '8px',
  padding: '16px',
  marginBottom: '16px',
  boxShadow: 'inset 0 0 20px rgba(224,64,251,0.05)'
}

export function PrivacyPolicy() {
  return (
    <div style={{ minHeight: '100vh', background: 'linear-gradient(135deg, #000000 0%, #0d0d12 100%)', fontFamily: "'Space Grotesk', 'Inter', sans-serif" }}>
      <Helmet>
        <title>Privacy Policy | The Caption Studio</title>
        <meta name="description" content="The Caption Studio privacy policy. Your photos are never stored or shared. We process everything locally and delete images immediately after caption generation." />
        <link rel="canonical" href="https://thecaption.studio/privacy" />
      </Helmet>

      {/* Header */}
      <header style={{ position: 'sticky', top: 0, zIndex: 50, background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(10px)', WebkitBackdropFilter: 'blur(10px)', borderBottom: '1px solid rgba(255,255,255,0.08)', padding: '12px 24px' }}>
        <nav style={{ maxWidth: '900px', margin: '0 auto', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Link to="/"><Logo /></Link>
          <Link to="/"
            style={{ display: 'flex', alignItems: 'center', gap: '6px', color: 'rgba(255,255,255,0.5)', textDecoration: 'none', fontSize: '0.9rem', transition: 'color 0.2s' }}
            onMouseEnter={e => { e.currentTarget.style.color = '#e040fb'; (e.currentTarget.style as any).textShadow = '0 0 10px rgba(224,64,251,0.4)' }}
            onMouseLeave={e => { e.currentTarget.style.color = 'rgba(255,255,255,0.5)'; (e.currentTarget.style as any).textShadow = 'none' }}
          >
            <ArrowLeft size={16} /> Back to Home
          </Link>
        </nav>
      </header>

      {/* Content */}
      <main style={{ maxWidth: '900px', margin: '0 auto', padding: '40px 24px' }}>

        {/* Glass card wrapper */}
        <div style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.1)', backdropFilter: 'blur(12px)', WebkitBackdropFilter: 'blur(12px)', borderRadius: '24px', padding: '40px', marginBottom: '40px' }}>

          <h1 style={{ fontSize: '2.5rem', fontWeight: 700, color: '#ffffff', marginBottom: '8px' }}>Privacy Policy</h1>
          <p style={{ color: 'rgba(255,255,255,0.4)', marginBottom: '40px', fontSize: '0.9rem' }}>
            Last updated: {new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
          </p>

          {/* Introduction */}
          <section style={{ marginBottom: '32px' }}>
            <h2 style={{ fontSize: '1.4rem', fontWeight: 600, color: '#ffffff', marginBottom: '12px' }}>Introduction</h2>
            <p style={{ color: '#cccccc', marginBottom: '16px', lineHeight: 1.7 }}>
              Welcome to The Caption Studio. We respect your privacy and are committed to protecting your personal data.
              This privacy policy explains how we handle your information when you use our service.
            </p>
            <div style={glassAlert}>
              <p style={{ color: '#D1D1F7', fontWeight: 600, marginBottom: '4px' }}>🔒 Privacy-First Design</p>
              <p style={{ color: 'rgba(209,209,247,0.8)', fontSize: '0.9rem' }}>
                Your images are analyzed entirely in your browser and never uploaded to our servers.
                Only text data (keywords and preferences) is sent to generate captions.
              </p>
            </div>
          </section>

          {/* Information We Collect */}
          <section style={{ marginBottom: '32px' }}>
            <h2 style={{ fontSize: '1.4rem', fontWeight: 600, color: '#ffffff', marginBottom: '12px' }}>Information We Collect</h2>
            <h3 style={{ fontSize: '1.1rem', fontWeight: 600, color: '#D1D1F7', marginBottom: '10px' }}>Images You Upload</h3>
            <div style={glassAlert}>
              <p style={{ color: '#D1D1F7', fontWeight: 600, marginBottom: '8px' }}>✨ Browser-Only Processing</p>
              <p style={{ color: 'rgba(209,209,247,0.8)', marginBottom: '8px', fontSize: '0.9rem' }}>
                <strong style={{ color: '#ffffff' }}>Your images NEVER leave your device.</strong> All image analysis happens locally in your browser using AI technology (COCO-SSD).
              </p>
              <p style={{ color: 'rgba(209,209,247,0.7)', fontSize: '0.85rem' }}>
                No image uploads, no server storage, complete privacy. Your photos stay on your device at all times.
              </p>
            </div>
            <h3 style={{ fontSize: '1.1rem', fontWeight: 600, color: '#D1D1F7', marginBottom: '10px' }}>Text Data We Process</h3>
            <p style={{ color: '#cccccc', marginBottom: '12px', lineHeight: 1.7 }}>To generate captions, we receive and process:</p>
            <ul style={{ color: '#cccccc', paddingLeft: '20px', lineHeight: 2 }}>
              <li><strong style={{ color: '#ffffff' }}>AI-detected keywords</strong> from your image</li>
              <li><strong style={{ color: '#ffffff' }}>Your custom keywords</strong> that you optionally add</li>
              <li><strong style={{ color: '#ffffff' }}>Selected mood</strong> (Lowkey, Aesthetic, etc.)</li>
              <li><strong style={{ color: '#ffffff' }}>Caption preferences</strong> (length, style)</li>
            </ul>
          </section>

          {/* How We Use */}
          <section style={{ marginBottom: '32px' }}>
            <h2 style={{ fontSize: '1.4rem', fontWeight: 600, color: '#ffffff', marginBottom: '12px' }}>How We Use Your Information</h2>
            <ul style={{ color: '#cccccc', paddingLeft: '20px', lineHeight: 2 }}>
              <li>Generate AI-powered captions based on keywords (not images)</li>
              <li>Improve our AI models and service quality</li>
              <li>Analyze usage patterns to enhance user experience</li>
              <li>Prevent abuse and ensure service security</li>
            </ul>
          </section>

          {/* Data Storage */}
          <section style={{ marginBottom: '32px' }}>
            <h2 style={{ fontSize: '1.4rem', fontWeight: 600, color: '#ffffff', marginBottom: '12px' }}>Data Storage and Security</h2>
            <ul style={{ color: '#cccccc', paddingLeft: '20px', lineHeight: 2, marginBottom: '16px' }}>
              <li><strong style={{ color: '#ffffff' }}>Images are NEVER uploaded or stored</strong> — they remain on your device only</li>
              <li>Only text keywords are transmitted (encrypted via HTTPS)</li>
              <li>Generated captions are not permanently stored</li>
              <li>We do not sell or share your personal information</li>
            </ul>
            <div style={glassAlertPink}>
              <p style={{ color: '#FFB6C1', fontWeight: 600, marginBottom: '4px' }}>🛡️ Maximum Privacy Protection</p>
              <p style={{ color: 'rgba(255,182,193,0.8)', fontSize: '0.9rem' }}>
                Our browser-only architecture ensures your images never leave your device. We literally cannot access your images even if we wanted to.
              </p>
            </div>
          </section>

          {/* Cookies */}
          <section style={{ marginBottom: '32px' }}>
            <h2 style={{ fontSize: '1.4rem', fontWeight: 600, color: '#ffffff', marginBottom: '12px' }}>Cookies and Tracking</h2>
            <p style={{ color: '#cccccc', marginBottom: '12px', lineHeight: 1.7 }}>We use minimal cookies and local storage to:</p>
            <ul style={{ color: '#cccccc', paddingLeft: '20px', lineHeight: 2 }}>
              <li>Remember your preferences (mood, caption length)</li>
              <li>Maintain your session</li>
              <li>Analyze site traffic and usage patterns</li>
            </ul>
          </section>

          {/* Your Rights */}
          <section style={{ marginBottom: '32px' }}>
            <h2 style={{ fontSize: '1.4rem', fontWeight: 600, color: '#ffffff', marginBottom: '12px' }}>Your Rights</h2>
            <ul style={{ color: '#cccccc', paddingLeft: '20px', lineHeight: 2 }}>
              <li>Access any personal data we hold about you</li>
              <li>Request deletion of your data</li>
              <li>Opt-out of analytics and tracking</li>
              <li>Request information about how your data is used</li>
            </ul>
          </section>

          {/* Contact */}
          <section style={{ marginBottom: '32px' }}>
            <h2 style={{ fontSize: '1.4rem', fontWeight: 600, color: '#ffffff', marginBottom: '12px' }}>Contact Us</h2>
            <p style={{ color: '#cccccc', lineHeight: 1.7 }}>
              If you have any questions about this Privacy Policy, please contact us at:{' '}
              <a href="mailto:hello.captiostudio@gmail.com" style={{ color: '#8E8FFA', textDecoration: 'none' }}
                onMouseEnter={e => (e.currentTarget.style.color = '#e040fb')}
                onMouseLeave={e => (e.currentTarget.style.color = '#8E8FFA')}
              >
                hello.captiostudio@gmail.com
              </a>
            </p>
          </section>

          {/* Summary - high contrast glass card */}
          <section style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: '16px', padding: '28px' }}>
            <h2 style={{ fontSize: '1.4rem', fontWeight: 600, color: '#ffffff', marginBottom: '20px' }}>Summary: Your Privacy Matters</h2>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
              {[
                { label: 'Images never uploaded', desc: 'analyzed locally in your browser' },
                { label: 'No image storage', desc: 'we literally cannot access your photos' },
                { label: 'Only text keywords sent', desc: 'no personally identifiable information' },
                { label: 'Maximum privacy', desc: 'browser-only processing ensures complete control' },
              ].map((item, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                  <span style={{ color: '#e040fb', fontSize: '1.2rem', flexShrink: 0, filter: 'drop-shadow(0 0 6px rgba(224,64,251,0.6))' }}>✓</span>
                  <p style={{ color: '#cccccc', margin: 0 }}>
                    <strong style={{ color: '#ffffff' }}>{item.label}</strong> — {item.desc}
                  </p>
                </div>
              ))}
            </div>
          </section>

        </div>
      </main>
    </div>
  )
}
